import React, { useState } from 'react';
import PropTypes from 'prop-types';
import LoadIcon from '../../LoadIcon';
import importTicketCodes from '../../../api/graphql/mutations/importTicketCodes';
import { parseCodesCsv, chunkRows, buildImportBlockers } from '../../../utils/loyaltyPrizes';
import './loyalty.css';

// The server caps a single importTicketCodes call at 5,000 rows and REJECTS (rather
// than truncates) anything larger — see the mutation wrapper for the full contract.
const CHUNK_SIZE = 5000;

const MANIFEST_COLUMNS = ['code', 'link', 'scope'];

const formatKb = bytes => `${(bytes / 1024).toFixed(1)} KB`;

const LoyaltyImportModal = ({ prize, onClose, onImported }) => {
  // pick: no file yet. review: parsed, showing manifest/tally/preview/problems.
  // importing: chunks are being sent. done: server responses accumulated.
  const [phase, setPhase] = useState('pick');
  const [fileMeta, setFileMeta] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [readError, setReadError] = useState(null);
  const [progress, setProgress] = useState({
    doneChunks: 0, totalChunks: 0, inserted: 0, rejected: 0, errors: [],
  });

  const stopPropagation = event => event.stopPropagation();

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setReadError(null);

    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const result = parseCodesCsv(text);
      const dataLineCount = Math.max(0, text.split(/\r?\n/).filter(line => line.trim() !== '').length - 1);
      setFileMeta({ name: file.name, sizeLabel: formatKb(file.size), dataLineCount });
      setParsed(result);
      setPhase('review');
    };
    reader.onerror = () => setReadError(`Could not read ${file.name}.`);
    reader.readAsText(file);
  };

  const handlePickAgain = () => {
    setPhase('pick');
    setFileMeta(null);
    setParsed(null);
  };

  const targetScope = prize ? prize.slugName : ((parsed && parsed.scopes[0]) || null);
  const blockers = parsed ? buildImportBlockers(parsed, prize) : [];
  const canImport = !!parsed && blockers.length === 0 && parsed.rows.length > 0 && !!targetScope;

  const handleConfirm = async () => {
    if (!canImport) return;
    const chunks = chunkRows(parsed.rows, CHUNK_SIZE);
    setPhase('importing');
    setProgress({
      doneChunks: 0, totalChunks: chunks.length, inserted: 0, rejected: 0, errors: [],
    });

    let inserted = 0;
    let rejected = 0;
    let errors = [];

    for (let i = 0; i < chunks.length; i++) {
      // Sequential on purpose: parallel chunks would let the server's per-call
      // validation (and the resulting error list) arrive out of order, and there is
      // no benefit to racing calls the operator is watching progress on.
      // eslint-disable-next-line no-await-in-loop
      const result = await importTicketCodes({ scope: targetScope, rows: JSON.stringify(chunks[i]) });
      if (result && result.isAuth) {
        inserted += result.inserted || 0;
        rejected += result.rejected || 0;
        if (result.errors && result.errors.length) errors = errors.concat(result.errors);
      } else {
        errors = errors.concat([`Batch ${i + 1} of ${chunks.length} failed — not authorised, or a network error.`]);
      }
      setProgress({
        doneChunks: i + 1, totalChunks: chunks.length, inserted, rejected, errors,
      });
    }

    setPhase('done');
    if (onImported) onImported();
  };

  let body;
  let foot;

  if (phase === 'pick') {
    body = (
      <div className="im-body">
        <div className="im-pick">
          <p>
            Choose a CSV with
            {' '}
            <code className="code">code,link,scope</code>
            {' '}
            columns. Repeated codes are fine — that is how a multi-use coupon is
            stored, and they are never deduplicated.
          </p>
          <label className="btn p" htmlFor="loyalty-import-file">
            Choose file…
            <input
              id="loyalty-import-file"
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </label>
        </div>
        {readError ? (
          <div className="im-probs">
            <b>{readError}</b>
          </div>
        ) : null}
      </div>
    );
    foot = (
      <div className="im-foot">
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
      </div>
    );
  } else if (phase === 'review') {
    const rowCount = parsed.rows.length;
    const sample = rowCount > 0 ? parsed.rows[0] : null;

    body = (
      <div className="im-body">
        <div className="im-slip">
          {MANIFEST_COLUMNS.map((col) => {
            const found = parsed.headers.indexOf(col) !== -1;
            return (
              <div className="im-slip-row" key={col}>
                <span className={`im-mark ${found ? 'ok' : 'bad'}`}>{found ? '✓' : '–'}</span>
                <span className="im-col">{col}</span>
                <span className="im-sample">{found && sample ? sample[col] : 'not found'}</span>
                <span className={`im-verdict ${found ? 'ok' : 'bad'}`}>{found ? 'required · found' : 'required · missing'}</span>
              </div>
            );
          })}
          {parsed.extraColumns.map(col => (
            <div className="im-slip-row" key={col}>
              <span className="im-mark">–</span>
              <span className="im-col">{col}</span>
              <span className="im-sample">not imported</span>
              <span className="im-verdict">extra · ignored</span>
            </div>
          ))}
        </div>

        <div className="im-tally">
          <div>
            <span className="label">Rows read</span>
            <span className="v">{fileMeta.dataLineCount}</span>
          </div>
          <div>
            <span className="label">Will insert</span>
            <span className="v ok">{blockers.length === 0 ? rowCount : 0}</span>
          </div>
          <div>
            <span className="label">Repeated codes</span>
            <span className="v">{parsed.duplicateCodeCount}</span>
          </div>
          <div>
            <span className="label">Rejected</span>
            <span className="v">{parsed.lineErrors.length}</span>
          </div>
        </div>

        <div className="im-incoming">
          <div className="im-cap">
            <span>
              Adding to
              {' '}
              <b>{targetScope || '—'}</b>
            </span>
            <span>{`${rowCount} row${rowCount === 1 ? '' : 's'}`}</span>
          </div>
          {rowCount === 0 ? (
            <div className="im-empty">No rows to import.</div>
          ) : (
            <>
              <table className="im-preview">
                <tbody>
                  {parsed.rows.slice(0, 8).map((row, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={`${row.code}-${index}`}>
                      <td className="c">{row.code}</td>
                      <td className="l">{row.link}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rowCount > 8 ? <div className="im-more">{`+ ${rowCount - 8} more`}</div> : null}
            </>
          )}
        </div>

        {parsed.duplicateCodeCount > 0 ? (
          <div className="im-probs im-probs--info">
            <b>Repeated codes</b>
            <ul>
              <li>{`${parsed.duplicateCodeCount} row${parsed.duplicateCodeCount > 1 ? 's' : ''} repeat a code; this is how a multi-use coupon is stored.`}</li>
            </ul>
          </div>
        ) : null}

        {blockers.length > 0 ? (
          <div className="im-probs">
            <b>{`${blockers.length} thing${blockers.length > 1 ? 's' : ''} to fix before importing`}</b>
            <ul>
              {blockers.map(message => <li key={message}>{message}</li>)}
            </ul>
          </div>
        ) : null}
      </div>
    );

    foot = (
      <div className="im-foot">
        <button type="button" className="btn" onClick={handlePickAgain}>Choose a different file</button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn p" disabled={!canImport} onClick={handleConfirm}>
            {`Import ${rowCount} code${rowCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    );
  } else if (phase === 'importing') {
    body = (
      <div className="im-body im-progress">
        <LoadIcon />
        <div className="im-progress-txt">
          {`Importing batch ${progress.doneChunks} of ${progress.totalChunks}… ${progress.inserted} inserted so far`}
        </div>
      </div>
    );
    foot = <div className="im-foot" />;
  } else {
    body = (
      <div className="im-body">
        <div className="im-tally">
          <div>
            <span className="label">Batches</span>
            <span className="v">{progress.totalChunks}</span>
          </div>
          <div>
            <span className="label">Inserted</span>
            <span className="v ok">{progress.inserted}</span>
          </div>
          <div>
            <span className="label">Rejected</span>
            <span className={`v${progress.rejected ? ' bad' : ''}`}>{progress.rejected}</span>
          </div>
          <div>
            <span className="label">Errors</span>
            <span className={`v${progress.errors.length ? ' bad' : ''}`}>{progress.errors.length}</span>
          </div>
        </div>
        {progress.errors.length > 0 ? (
          <div className="im-probs">
            <b>{`${progress.errors.length} error${progress.errors.length > 1 ? 's' : ''} from the server`}</b>
            <ul>
              {progress.errors.slice(0, 10).map(message => <li key={message}>{message}</li>)}
            </ul>
          </div>
        ) : null}
      </div>
    );
    foot = (
      <div className="im-foot">
        <span className="im-note">Done — the ledger has been refreshed.</span>
        <button type="button" className="btn p" onClick={onClose}>Close</button>
      </div>
    );
  }

  return (
    <div className="loyalty">
      <div className="scrim" onClick={phase === 'importing' ? undefined : onClose}>
        <div className="modal im-modal" onClick={stopPropagation}>
          {phase !== 'importing' ? (
            <button type="button" className="x" onClick={onClose} aria-label="Close">✕</button>
          ) : null}
          <div className="im-head">
            <h2>Import codes</h2>
            {fileMeta ? (
              <span className="im-file">
                {`${fileMeta.name} · ${fileMeta.sizeLabel} · ${fileMeta.dataLineCount + 1} lines`}
              </span>
            ) : (
              <span className="im-file">
                {prize ? `Adding to ${prize.slugName}` : 'No prize selected — the scope comes from the file'}
              </span>
            )}
          </div>
          {body}
          {foot}
        </div>
      </div>
    </div>
  );
};

LoyaltyImportModal.propTypes = {
  prize: PropTypes.shape({
    slugName: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onImported: PropTypes.func,
};

LoyaltyImportModal.defaultProps = {
  prize: null,
  onImported: null,
};

export default LoyaltyImportModal;
